import { useState } from 'react';
import {
  MAX_SUBJECT_LENGTH,
  MIN_DESCRIPTION_LENGTH,
  MIN_SUBJECT_LENGTH,
} from 'app/services/bugReportApi';
import type { IBugReportCreated } from 'app/services/bugReportApi';
import { useCreateFeatureRequestMutation } from 'app/services/featureRequestApi';
import {
  AreaField,
  AttachmentField,
  DiscardConfirm,
  EmailField,
  FeedbackContext,
  FeedbackDone,
  MinLengthHint,
  autoGrow,
  defaultArea,
  feedbackErrorMessage,
  useAttachments,
  useCloseGuard,
} from './FeedbackForm';

/**
 * "Request a Feature" popup, opened from the header's More Actions menu just
 * below Report a Bug, and built from the same parts (`FeedbackForm.tsx`): the
 * same context strip, Your email, Area and file row, the same discard and Esc
 * rules, the same stylesheet. BugReportPopup.tsx's header explains the shared
 * choices; this file only has what differs.
 *
 * It goes to the same pipeline as a bug report, tagged `kind: "feature_request"`
 * (`featureRequestApi.ts`), which routes it to the team rather than to support
 * triage. So it asks for what makes a request something we can weigh: what the
 * user wants to be able to do, and — optional, but prompted for — why, and what
 * they do today instead. There is no Severity or Steps to reproduce.
 */

interface FeatureRequestPopupProps {
  onClose: () => void;
  /** GlobalHeader's `appName`; pre-selects Area as on the bug form. */
  appName?: string;
}

const ERRORS = {
  generic: 'The request could not be sent. Please try again, or email support directly.',
  tooMany: 'Too many feature requests in the last hour. Please try again later.',
};

const FeatureRequestPopup = ({ onClose, appName }: FeatureRequestPopupProps) => {
  const [createFeatureRequest, { isLoading }] = useCreateFeatureRequestMutation();

  const [area, setArea] = useState(() => defaultArea(appName));
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [why, setWhy] = useState('');
  const [reqError, setReqError] = useState('');
  const [created, setCreated] = useState<IBugReportCreated | null>(null);
  const file = useAttachments(setReqError);

  // The server's minimums (api/rest_bug_report/serializers.py, shared by both
  // forms). Title and the description are the only gates.
  const subjectLength = subject.trim().length;
  const descriptionLength = description.trim().length;
  const canSubmit =
    subjectLength >= MIN_SUBJECT_LENGTH &&
    descriptionLength >= MIN_DESCRIPTION_LENGTH &&
    !isLoading;

  const hasDraft = Boolean(subject.trim() || description.trim() || why.trim() || file.attachments.length);
  const { confirmDiscard, requestClose, keepEditing, discard } = useCloseGuard({
    isLoading,
    hasDraft,
    done: Boolean(created),
    onClose,
  });

  const onSubmit = async () => {
    if (!canSubmit) return;
    setReqError('');
    try {
      const request = await createFeatureRequest({
        subject,
        description,
        why,
        area,
        attachments: file.attachments,
      }).unwrap();
      setCreated(request);
    } catch (error) {
      setReqError(feedbackErrorMessage(error, ERRORS));
    }
  };

  if (created) {
    return (
      <FeedbackDone title="Thanks — request sent" onClose={onClose}>
        {/* Asked for in the thread, 2026-10-30: the confirmation repeats the
            Title that was just sent, so the requester can see WHICH request the
            reference belongs to — they can send several in a row. It is the
            subject the POST carried (trimmed, as the serializer stores it), not
            anything the server echoes back: the create response is only
            `id` / `reference` / `created_at`. */}
        <p className="feedback-subject">{subject.trim()}</p>
        <p>
          Your request has been sent to the Kinetix team. If you follow up, quote Reference{' '}
          <strong>{created.reference}</strong>.
        </p>
        {/* Asked for in the thread, 2026-10-28: the acknowledgement names where the
            request goes next, so the requester is not left wondering what happens
            to it. Muted and under the reference line, because it is the same text
            on every request. */}
        <p className="feedback-note">
          Your feature request has been submitted to the Kinetix Software product management team
          for review and potential future roadmap consideration. A copy has also been forwarded to
          the manufacturer&apos;s CMS Administrator.
        </p>
        <p className="feedback-note">We value your feedback.</p>
      </FeedbackDone>
    );
  }

  return (
    <>
      <div id="popup-content" className="popup-wrapper feedback-popup active">
        <div className="popup-content">
          <h2 className="popup-heading">
            <span className="popup-heading-text">Request a Feature</span>
            <span className="popup-heading-note">Sent to the Kinetix team</span>
          </h2>
          <FeedbackContext />
          <div className="popup-content-part feedback-fields">
            <EmailField
              id="feature_request_email"
              hint="Your account address. Your receipt and the Kinetix team's reply come here."
              noEmailHint="Your account has no email address, so the Kinetix team cannot reply by email."
            />

            <AreaField id="feature_request_area" value={area} onChange={setArea} />

            <div className="feedback-field">
              <label className="label required" htmlFor="feature_request_subject">
                Title
              </label>
              <div className="feedback-control">
                <input
                  id="feature_request_subject"
                  type="text"
                  autoFocus
                  maxLength={MAX_SUBJECT_LENGTH}
                  placeholder="Export the coil schedule to Excel"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <MinLengthHint min={MIN_SUBJECT_LENGTH} length={subjectLength} />
              </div>
            </div>

            <div className="feedback-field">
              <label className="label required" htmlFor="feature_request_description">
                What would you like to be able to do?
              </label>
              <div className="feedback-control">
                {/* Opens at two rows and grows, as What happened? does on the
                    bug form. */}
                <textarea
                  id="feature_request_description"
                  className="feedback-compact-text"
                  ref={autoGrow}
                  rows={2}
                  placeholder="Describe the outcome you want, not only the button."
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    autoGrow(e.target);
                  }}
                />
                <MinLengthHint min={MIN_DESCRIPTION_LENGTH} length={descriptionLength} />
              </div>
            </div>

            <div className="feedback-field">
              <label className="label" htmlFor="feature_request_why">
                Why, and what do you do today instead?
              </label>
              <div className="feedback-control">
                {/* Optional, but the placeholder asks for it: the workaround in
                    use today is what tells us how much a request is worth. */}
                <textarea
                  id="feature_request_why"
                  ref={autoGrow}
                  rows={4}
                  placeholder={
                    'Optional, but it helps us most. E.g. "We quote from a spreadsheet, so today I ' +
                    'copy the coil table into it by hand for every project."'
                  }
                  value={why}
                  onChange={(e) => {
                    setWhy(e.target.value);
                    autoGrow(e.target);
                  }}
                />
              </div>
            </div>

            <AttachmentField
              id="feature_request_attachment"
              label="Screenshots or sketches"
              attachments={file.attachments}
              fileInputRef={file.fileInputRef}
              onPick={file.pick}
              onRemove={file.remove}
            />
          </div>
          <div className="popup-control-buttons">
            <span className="popup-note">* Required field</span>
            {reqError && <div className="req-error">{reqError}</div>}
            <button
              className={`btn highlighted-btn${canSubmit ? '' : ' non-clickable'}`}
              type="button"
              onClick={onSubmit}
            >
              {isLoading ? 'Sending…' : 'Send Request'}
            </button>
            {/* `requestClose`, not `onClose`: this is also what Esc clicks. */}
            <button
              className={`btn not-highlighted-btn${isLoading ? ' non-clickable' : ''}`}
              type="button"
              name="cancel"
              onClick={requestClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      {confirmDiscard && (
        <DiscardConfirm
          title="Discard this feature request?"
          onKeep={keepEditing}
          onDiscard={discard}
        >
          <p>What you have written will be lost. Nothing has been sent yet.</p>
        </DiscardConfirm>
      )}
    </>
  );
};

export default FeatureRequestPopup;
